// tabs/ThinkyBaaSIntegration.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlug, faCheckCircle, faUpload, faPlay } from "@fortawesome/free-solid-svg-icons";

const dummyIntegrations = [
  {
    id: 1,
    name: "ERP System Integration",
    description: "Syncs financial transactions between ERP and blockchain ledger.",
    connected: false,
    executed: false,
    verified: false,
    log: [],
    precondition: true, // simulate API readiness
  },
  {
    id: 2,
    name: "IoT Sensor Integration",
    description: "Feeds supply chain milestones automatically to blockchain.",
    connected: false,
    executed: false,
    verified: false,
    log: [],
    precondition: false, // simulate sensor offline
  },
  {
    id: 3,
    name: "Education Platform API",
    description: "Pushes course completion events to blockchain certificate ledger.",
    connected: false,
    executed: false,
    verified: false,
    log: [],
    precondition: true,
  },
];

const ThinkyBaaSIntegration = () => {
  const [integrations, setIntegrations] = useState(dummyIntegrations.map(i => ({ ...i, log: i.log || [] })));

  // Connect API
  const connectAPI = (id) => {
    setIntegrations(prev =>
      prev.map(i =>
        i.id === id && !i.connected
          ? { ...i, connected: true, log: [...(i.log || []), { event: "API Connected", time: new Date().toLocaleTimeString() }] }
          : i
      )
    );
  };

  // Execute API workflow
  const executeIntegration = (id) => {
    setIntegrations(prev =>
      prev.map(i => {
        if (i.id === id) {
          if (!i.precondition) {
            return { ...i, log: [...(i.log || []), { event: "Execution blocked: API not ready", time: new Date().toLocaleTimeString() }] };
          }
          return { ...i, executed: true, log: [...(i.log || []), { event: "Integration Executed", time: new Date().toLocaleTimeString() }] };
        }
        return i;
      })
    );
  };

  // Verify integration
  const verifyIntegration = (id) => {
    setIntegrations(prev =>
      prev.map(i =>
        i.id === id && i.executed && !i.verified
          ? { ...i, verified: true, log: [...(i.log || []), { event: "Verified Data Flow", time: new Date().toLocaleTimeString() }] }
          : i
      )
    );
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl shadow-lg space-y-6">
      <div className="flex items-center mb-4">
        <FontAwesomeIcon icon={faPlug} className="text-blue-400 text-xl lg:text-2xl mr-4" />
        <h2 className="text-lg lg:text-xl font-bold">Integration & APIs Workflow</h2>
      </div>

      <p className="text-gray-300 text-sm">
        This page simulates a blockchain <span className="font-semibold">Integration & APIs workflow</span>:
        Connect → Execute → Verify. Event logs track interactions and data flow.
      </p>

      <div className="space-y-4">
        {integrations.map(i => (
          <div key={i.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <h3 className="text-xl font-semibold mb-1">{i.name}</h3>
            <p className="text-gray-400 mb-2">{i.description}</p>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`px-2 py-1 rounded-sm text-sm ${i.connected ? "bg-green-600" : "bg-yellow-600"}`}>
                {i.connected ? "Connected ✅" : "Pending Connection ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${i.executed ? "bg-green-600" : "bg-yellow-600"}`}>
                {i.executed ? "Executed ✅" : "Pending Execution ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${i.verified ? "bg-green-600" : "bg-yellow-600"}`}>
                {i.verified ? "Verified ✅" : "Pending Verification ⏳"}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                disabled={i.connected}
                onClick={() => connectAPI(i.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  i.connected ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faUpload} className="mr-1" /> Connect
              </button>
              <button
                disabled={!i.connected || i.executed}
                onClick={() => executeIntegration(i.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !i.connected || i.executed ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-1" /> Execute
              </button>
              <button
                disabled={!i.executed || i.verified}
                onClick={() => verifyIntegration(i.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !i.executed || i.verified ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verify
              </button>
            </div>

            <div className="mt-3 bg-gray-900 p-2 rounded-lg border border-gray-700">
              <h4 className="text-gray-300 font-semibold mb-1">Event Log:</h4>
              {(!i.log || i.log.length === 0) ? (
                <p className="text-gray-500 text-sm">No events yet.</p>
              ) : (
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {i.log?.map((log, idx) => (
                    <li key={idx}>
                      [{log.time}] {log.event}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThinkyBaaSIntegration;
